import { useState, useEffect, useRef } from "react";
import { supabase } from "@/lib/supabaseClient";
import { uploadInvoiceFiles } from "@/lib/uploadInvoiceFile";
import { useToast } from "@/lib/useToast";
import { Toast } from "@/components/ui";

interface Quote {
  id: string;
  file_url: string;
  version: number;
  signed: boolean;
  upload_group_id: string;
  group_position: number;
  created_at: string;
}

interface Invoice {
  id: string;
  file_url: string;
  invoice_type: "deposit" | "final";
  version: number;
  upload_group_id: string;
  group_position: number;
  created_at: string;
}

interface AttachmentFile {
  id: string;
  type: "quote" | "invoice";
  file_url: string;
  label: string;
  version?: number;
}

interface QuoteUploadResponse {
  uploadGroupId?: string;
  message?: string;
}

// Fichier personnalisé (non stocké dans Supabase, envoyé en one-shot)
interface CustomFile {
  id: string;
  name: string;
  type: string;
  size: number;
  base64: string;
}

interface AttachmentSelectorProps {
  caseId: string;
  onClose: () => void;
  onConfirm: (selectedFiles: AttachmentFile[], customFiles: CustomFile[]) => void;
}

// Limite de 20 Mo pour les fichiers personnalisés
const MAX_CUSTOM_FILE_SIZE = 20 * 1024 * 1024;

export function AttachmentSelector({ caseId, onClose, onConfirm }: AttachmentSelectorProps) {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const { toast, showToast, hideToast } = useToast();
  
  // Fichiers personnalisés (non stockés, envoi one-shot)
  const [customFiles, setCustomFiles] = useState<CustomFile[]>([]);
  
  // Refs pour les inputs de fichiers
  const quoteFileInputRef = useRef<HTMLInputElement>(null);
  const depositFileInputRef = useRef<HTMLInputElement>(null);
  const finalFileInputRef = useRef<HTMLInputElement>(null);
  const customFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchAttachments = async () => {
      setLoading(true);

      // Récupérer les devis
      const { data: quotesData } = await supabase
        .from("quotes")
        .select("*")
        .eq("case_id", caseId)
        .is("archived_at", null)
        .order("created_at", { ascending: false });

      // Récupérer les factures
      const { data: invoicesData } = await supabase
        .from("invoices")
        .select("*")
        .eq("case_id", caseId)
        .is("archived_at", null)
        .order("created_at", { ascending: false });

      setQuotes((quotesData as Quote[]) || []);
      setInvoices((invoicesData as Invoice[]) || []);
      setLoading(false);
    };

    fetchAttachments();
  }, [caseId]);

  // Refresh attachments after upload
  const refreshAttachments = async () => {
    const { data: quotesData } = await supabase
      .from("quotes")
      .select("*")
      .eq("case_id", caseId)
        .is("archived_at", null)
      .order("created_at", { ascending: false });

    const { data: invoicesData } = await supabase
      .from("invoices")
      .select("*")
      .eq("case_id", caseId)
        .is("archived_at", null)
      .order("created_at", { ascending: false });

    setQuotes((quotesData as Quote[]) || []);
    setInvoices((invoicesData as Invoice[]) || []);
  };

  // Helper pour convertir File en base64 (pour les devis)
  const toBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result !== "string") {
          reject(new Error("Lecture du fichier impossible"));
          return;
        }
        const comma = result.indexOf(",");
        resolve(comma >= 0 ? result.substring(comma + 1) : result);
      };
      reader.onerror = () => {
        reject(reader.error ?? new Error("Lecture du fichier impossible"));
      };
      reader.readAsDataURL(file);
    });

  // Upload de devis
  const handleQuoteUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const pdfFiles = Array.from(files).filter((file) => file.type === "application/pdf");
    if (pdfFiles.length === 0) {
      showToast({ message: "Veuillez sélectionner uniquement des fichiers PDF", type: "warning" });
      return;
    }

    setUploading(true);

    try {
      let uploadGroupId: string | undefined = undefined; // Sera défini après le 1er upload

      for (let i = 0; i < pdfFiles.length; i++) {
        const file = pdfFiles[i]!;

        // Vérifier la taille (3 Mo max)
        const MAX_SIZE = 3 * 1024 * 1024;
        if (file.size > MAX_SIZE) {
          showToast({ message: `${file.name}: Taille maximale de 3 Mo dépassée`, type: "warning" });
          continue;
        }

        const fileBase64 = await toBase64(file);
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData.session?.access_token;

        const response: Response = await fetch("/api/quotes/upload", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            caseId,
            fileName: file.name,
            fileType: file.type || "application/pdf",
            fileBase64,
            uploadGroupId, // undefined pour le 1er, puis réutilisé
            groupPosition: i + 1,
          }),
        });

        const payload = (await response.json()) as QuoteUploadResponse;

        if (!response.ok) {
          throw new Error(payload?.message ?? "Erreur lors de l'upload du devis");
        }

        // Récupérer l'uploadGroupId du 1er fichier pour les suivants
        if (i === 0) {
          uploadGroupId = payload.uploadGroupId;
          console.log(`✅ Groupe créé: ${uploadGroupId}`);
        }
      }

      await refreshAttachments();
      showToast({ 
        message: `${pdfFiles.length} devis uploadé${pdfFiles.length > 1 ? 's' : ''} avec succès`, 
        type: "success" 
      });
    } catch (err) {
      console.error("Erreur lors de l'upload des devis:", err);
      showToast({ 
        message: err instanceof Error ? err.message : "Erreur lors de l'upload", 
        type: "error" 
      });
    } finally {
      setUploading(false);
      if (quoteFileInputRef.current) {
        quoteFileInputRef.current.value = "";
      }
    }
  };

  // Upload de factures
  const handleInvoiceUpload = async (files: FileList | null, invoiceType: "deposit" | "final") => {
    if (!files || files.length === 0) return;

    const pdfFiles = Array.from(files).filter((file) => file.type === "application/pdf");
    if (pdfFiles.length === 0) {
      showToast({ message: "Veuillez sélectionner uniquement des fichiers PDF", type: "warning" });
      return;
    }

    setUploading(true);

    const result = await uploadInvoiceFiles(caseId, invoiceType, pdfFiles);

    if (result.success) {
      await refreshAttachments();
      showToast({ 
        message: `${pdfFiles.length} facture${pdfFiles.length > 1 ? 's' : ''} uploadée${pdfFiles.length > 1 ? 's' : ''} avec succès`, 
        type: "success" 
      });
    } else {
      showToast({ message: `Erreur lors de l'upload: ${result.error}`, type: "error" });
    }

    setUploading(false);

    if (invoiceType === "deposit" && depositFileInputRef.current) {
      depositFileInputRef.current.value = "";
    } else if (invoiceType === "final" && finalFileInputRef.current) {
      finalFileInputRef.current.value = "";
    }
  };

  // Upload de fichiers personnalisés (one-shot, non stockés)
  const handleCustomFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const newCustomFiles: CustomFile[] = [];

    for (const file of Array.from(files)) {
      // Vérifier la taille (20 Mo max)
      if (file.size > MAX_CUSTOM_FILE_SIZE) {
        showToast({ 
          message: `${file.name}: Taille maximale de 20 Mo dépassée`, 
          type: "warning" 
        });
        continue;
      }

      // Vérifier si le fichier n'est pas déjà ajouté
      if (customFiles.some(cf => cf.name === file.name && cf.size === file.size)) {
        showToast({ 
          message: `${file.name}: Fichier déjà ajouté`, 
          type: "warning" 
        });
        continue;
      }

      try {
        const base64 = await toBase64(file);
        newCustomFiles.push({
          id: `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: file.name,
          type: file.type || "application/octet-stream",
          size: file.size,
          base64,
        });
      } catch (err) {
        console.error(`Erreur lecture ${file.name}:`, err);
        showToast({ 
          message: `Erreur lors de la lecture de ${file.name}`, 
          type: "error" 
        });
      }
    }

    if (newCustomFiles.length > 0) {
      setCustomFiles(prev => [...prev, ...newCustomFiles]);
      showToast({ 
        message: `${newCustomFiles.length} fichier${newCustomFiles.length > 1 ? 's' : ''} ajouté${newCustomFiles.length > 1 ? 's' : ''}`, 
        type: "success" 
      });
    }

    if (customFileInputRef.current) {
      customFileInputRef.current.value = "";
    }
  };

  // Supprimer un fichier personnalisé
  const removeCustomFile = (id: string) => {
    setCustomFiles(prev => prev.filter(f => f.id !== id));
  };

  // Formater la taille du fichier
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  };

  const toggleSelection = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleConfirm = () => {
    const selectedFiles: AttachmentFile[] = [];

    // Ajouter les devis sélectionnés
    quotes.forEach((quote) => {
      if (selectedIds.has(quote.id)) {
        selectedFiles.push({
          id: quote.id,
          type: "quote",
          file_url: quote.file_url,
          label: `Devis V${quote.version}${quote.signed ? " (signé)" : ""}`,
          version: quote.version,
        });
      }
    });

    // Ajouter les factures sélectionnées
    invoices.forEach((invoice) => {
      if (selectedIds.has(invoice.id)) {
        const typeLabel = invoice.invoice_type === "deposit" ? "Acompte" : "Finale";
        selectedFiles.push({
          id: invoice.id,
          type: "invoice",
          file_url: invoice.file_url,
          label: `Facture ${typeLabel} V${invoice.version}`,
          version: invoice.version,
        });
      }
    });

    // Passer aussi les fichiers personnalisés
    onConfirm(selectedFiles, customFiles);
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              📎 Sélectionner les pièces jointes
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-light leading-none"
            >
              ×
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden flex">
            {/* Liste des fichiers (gauche) */}
            <div className="w-1/2 border-r border-gray-200 overflow-y-auto p-6 space-y-6">
              {uploading && (
                <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-indigo-700">
                    <div className="animate-spin">⏳</div>
                    <span className="font-medium">Upload en cours...</span>
                  </div>
                </div>
              )}
              {loading ? (
                <div className="text-center text-gray-500 py-8">Chargement...</div>
              ) : (
                <>
                  {/* Section Devis */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-900">
                        📄 DEVIS ({quotes.length})
                      </h3>
                      <button
                        onClick={() => quoteFileInputRef.current?.click()}
                        disabled={uploading}
                        className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 hover:bg-indigo-200 text-indigo-700 text-lg font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Ajouter un devis"
                      >
                        +
                      </button>
                    </div>
                    <input
                      ref={quoteFileInputRef}
                      type="file"
                      accept="application/pdf"
                      multiple
                      onChange={(e) => handleQuoteUpload(e.target.files)}
                      className="hidden"
                    />
                    {quotes.length === 0 ? (
                      <p className="text-sm text-gray-500">Aucun devis disponible</p>
                    ) : (
                      <div className="space-y-2">
                        {quotes.map((quote) => (
                          <label
                            key={quote.id}
                            className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={selectedIds.has(quote.id)}
                              onChange={() => toggleSelection(quote.id)}
                              className="w-4 h-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500"
                            />
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900">
                                Version {quote.version}
                                {quote.signed && (
                                  <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                    ✓ Signé
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500">
                                {new Date(quote.created_at).toLocaleDateString("fr-FR")}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                setPreviewUrl(quote.file_url);
                              }}
                              className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                            >
                              Preview
                            </button>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Section Factures Acompte */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-900">
                        💳 FACTURES ACOMPTE ({invoices.filter((i) => i.invoice_type === "deposit").length})
                      </h3>
                      <button
                        onClick={() => depositFileInputRef.current?.click()}
                        disabled={uploading}
                        className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 hover:bg-indigo-200 text-indigo-700 text-lg font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Ajouter une facture d'acompte"
                      >
                        +
                      </button>
                    </div>
                    <input
                      ref={depositFileInputRef}
                      type="file"
                      accept="application/pdf"
                      multiple
                      onChange={(e) => handleInvoiceUpload(e.target.files, "deposit")}
                      className="hidden"
                    />
                    {invoices.filter((i) => i.invoice_type === "deposit").length === 0 ? (
                      <p className="text-sm text-gray-500">Aucune facture d'acompte</p>
                    ) : (
                      <div className="space-y-2">
                        {invoices
                          .filter((i) => i.invoice_type === "deposit")
                          .map((invoice) => (
                            <label
                              key={invoice.id}
                              className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={selectedIds.has(invoice.id)}
                                onChange={() => toggleSelection(invoice.id)}
                                className="w-4 h-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500"
                              />
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900">
                                  Version {invoice.version}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {new Date(invoice.created_at).toLocaleDateString("fr-FR")}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setPreviewUrl(invoice.file_url);
                                }}
                                className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                              >
                                Preview
                              </button>
                            </label>
                          ))}
                      </div>
                    )}
                  </div>

                  {/* Section Factures Finales */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-900">
                        🧾 FACTURES FINALES ({invoices.filter((i) => i.invoice_type === "final").length})
                      </h3>
                      <button
                        onClick={() => finalFileInputRef.current?.click()}
                        disabled={uploading}
                        className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 hover:bg-indigo-200 text-indigo-700 text-lg font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Ajouter une facture finale"
                      >
                        +
                      </button>
                    </div>
                    <input
                      ref={finalFileInputRef}
                      type="file"
                      accept="application/pdf"
                      multiple
                      onChange={(e) => handleInvoiceUpload(e.target.files, "final")}
                      className="hidden"
                    />
                    {invoices.filter((i) => i.invoice_type === "final").length === 0 ? (
                      <p className="text-sm text-gray-500">Aucune facture finale</p>
                    ) : (
                      <div className="space-y-2">
                        {invoices
                          .filter((i) => i.invoice_type === "final")
                          .map((invoice) => (
                            <label
                              key={invoice.id}
                              className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={selectedIds.has(invoice.id)}
                                onChange={() => toggleSelection(invoice.id)}
                                className="w-4 h-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500"
                              />
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900">
                                  Version {invoice.version}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {new Date(invoice.created_at).toLocaleDateString("fr-FR")}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setPreviewUrl(invoice.file_url);
                                }}
                                className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                              >
                                Preview
                              </button>
                            </label>
                          ))}
                      </div>
                    )}
                  </div>

                  {/* Section Fichiers personnalisés (one-shot) */}
                  <div className="border-t border-gray-200 pt-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-900">
                        📁 AUTRES FICHIERS ({customFiles.length})
                      </h3>
                      <button
                        onClick={() => customFileInputRef.current?.click()}
                        className="flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-700 text-lg font-bold transition-colors"
                        title="Ajouter un fichier (plaquette, etc.)"
                      >
                        +
                      </button>
                    </div>
                    <input
                      ref={customFileInputRef}
                      type="file"
                      multiple
                      onChange={(e) => handleCustomFileUpload(e.target.files)}
                      className="hidden"
                    />
                    <p className="text-xs text-gray-500 mb-3">
                      Plaquettes commerciales, présentations... (max 20 Mo par fichier)
                      <br />
                      <span className="text-amber-600">⚠️ Ces fichiers ne sont pas stockés, envoi unique.</span>
                    </p>
                    {customFiles.length === 0 ? (
                      <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center">
                        <button
                          onClick={() => customFileInputRef.current?.click()}
                          className="text-sm text-gray-500 hover:text-indigo-600"
                        >
                          Cliquez ou glissez-déposez vos fichiers ici
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {customFiles.map((file) => (
                          <div
                            key={file.id}
                            className="flex items-center gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50"
                          >
                            <div className="text-2xl">
                              {file.type.includes("pdf") ? "📄" : 
                               file.type.includes("image") ? "🖼️" : 
                               file.type.includes("word") || file.type.includes("document") ? "📝" :
                               file.type.includes("excel") || file.type.includes("spreadsheet") ? "📊" :
                               file.type.includes("powerpoint") || file.type.includes("presentation") ? "📊" :
                               "📁"}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {file.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {formatFileSize(file.size)}
                              </div>
                            </div>
                            <button
                              onClick={() => removeCustomFile(file.id)}
                              className="text-red-600 hover:text-red-800 text-lg font-bold"
                              title="Supprimer"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Preview (droite) */}
            <div className="w-1/2 bg-gray-50 flex flex-col">
              {previewUrl ? (
                <div className="flex-1 p-4 flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-700">Prévisualisation</span>
                    <a
                      href={previewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                    >
                      Ouvrir dans un nouvel onglet ↗
                    </a>
                  </div>
                  <iframe
                    src={previewUrl}
                    className="flex-1 w-full border border-gray-300 rounded-lg bg-white"
                    title="Preview PDF"
                  />
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-400">
                  <div className="text-center">
                    <div className="text-4xl mb-2">👁️</div>
                    <p className="text-sm">Cliquez sur "Preview" pour voir un fichier</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
            <div className="text-sm text-gray-600">
              {selectedIds.size === 0 && customFiles.length === 0 ? (
                "Aucune pièce jointe sélectionnée"
              ) : (
                <div className="flex flex-col gap-0.5">
                  {selectedIds.size > 0 && (
                    <span className="font-medium text-gray-900">
                      📎 {selectedIds.size} devis/facture{selectedIds.size > 1 ? "s" : ""}
                    </span>
                  )}
                  {customFiles.length > 0 && (
                    <span className="font-medium text-amber-700">
                      📁 {customFiles.length} fichier{customFiles.length > 1 ? "s" : ""} personnalisé{customFiles.length > 1 ? "s" : ""}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleConfirm}
                disabled={selectedIds.size === 0 && customFiles.length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      </div>
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
        duration={toast.duration}
      />
    </>
  );
}
